import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Alert,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../layouts/page-header/PageHeader';
import { pcServiceUrl } from '../../config';

const MyAccess = () => {
  const { user, isAuthenticated } = useAuth();
  const [dealerAccess, setDealerAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  useEffect(() => {
    console.log('MyAccess - Component mounted/updated:', {
      isAuthenticated,
      hasUser: !!user,
      userRole: user?.role,
      userKeys: user ? Object.keys(user) : [],
      dealer_access: user?.dealer_access,
      firebase_user: user?.firebase_user,
    });

    // Only fetch if we have both authentication and user data
    if (isAuthenticated && user) {
      console.log('MyAccess - Attempting to fetch dealer access with user:', {
        uid: user.uid,
        email: user.email,
        role: user.role,
      });
      fetchDealerAccess();
    } else {
      console.log('MyAccess - Waiting for user data...');
      setIsLoading(true);
    }
  }, [isAuthenticated, user]);

  const fetchDealerAccess = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Log full user object for debugging
      console.log(
        'MyAccess - Full user object in fetchDealerAccess:',
        JSON.stringify(user, null, 2)
      );

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // First, get all dealers to find which one has invited this user
      const dealersResponse = await fetch(`${pcServiceUrl}/api/v1/dealer`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!dealersResponse.ok) {
        throw new Error('Failed to fetch dealers');
      }

      const dealersData = await dealersResponse.json();
      console.log('MyAccess - Dealers data:', dealersData);

      // Try each dealer to find team membership
      for (const dealer of dealersData.dealers) {
        console.log('MyAccess - Checking dealer:', dealer.id);

        const response = await fetch(`${pcServiceUrl}/api/v1/dealer/team`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            dealerId: dealer.id,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('MyAccess - Team data for dealer:', {
            dealerId: dealer.id,
            data,
          });

          // Find my invitation in the team members
          const myAccess = data.team_members?.find(
            (member) => member.id === user.uid
          );

          if (myAccess) {
            console.log('MyAccess - Found my access:', {
              myAccess,
              dealerName: dealer.name,
            });

            // Add dealer info
            myAccess.businessName = dealer.name;
            myAccess.dealerId = dealer.id;
            setDealerAccess(myAccess);
            setIsLoading(false);
            return;
          }
        }
      }

      // If we get here, no dealer access was found
      setError(
        'No dealer access invitations found. Please ensure you have been invited to a dealer team.'
      );
      setDealerAccess(null);
    } catch (err) {
      console.error('MyAccess - Error in fetchDealerAccess:', err);
      setError('Failed to load dealer access. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccessAction = async (action) => {
    try {
      if (!dealerAccess?.dealerId) {
        throw new Error('No dealer access information found.');
      }

      setActionStatus({ loading: true, error: null, success: false });

      const token = localStorage.getItem('token');
      console.log('MyAccess - Making action API request:', {
        action,
        dealerId: dealerAccess.dealerId,
        hasToken: !!token,
        url: `${pcServiceUrl}/api/v1/dealer/team/${action}`,
      });

      const response = await fetch(
        `${pcServiceUrl}/api/v1/dealer/team/${action}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            dealerId: dealerAccess.dealerId,
            userId: user.uid,
          }),
        }
      );

      console.log('MyAccess - Action API response:', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('MyAccess - Action API error:', errorData);
        throw new Error(
          errorData.message || `Failed to ${action} dealer access`
        );
      }

      setActionStatus({ loading: false, error: null, success: true });
      fetchDealerAccess(); // Refresh the access data
    } catch (err) {
      console.error(`MyAccess - Error in handleAccessAction:`, err);
      setActionStatus({ loading: false, error: err.message, success: false });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'approved':
        return <Badge bg="success">Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge bg="danger">Admin</Badge>;
      case 'manager':
        return <Badge bg="primary">Manager</Badge>;
      case 'employee':
        return <Badge bg="info">Employee</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="site-content">
        <PageHeader
          title="My Access"
          description="Manage your dealer access permissions"
          breadCrumbItems={[
            { label: 'Home', path: '/' },
            { label: 'My Account', path: '/my-account' },
            { label: 'My Access', path: '#', active: true },
          ]}
        />
        <section className="section-ptb">
          <Container>
            <Alert variant="warning">Please log in to view this page.</Alert>
          </Container>
        </section>
      </div>
    );
  }

  if (user?.role !== 'DealerEmployee') {
    return (
      <div className="site-content">
        <PageHeader
          title="My Access"
          description="Manage your dealer access permissions"
          breadCrumbItems={[
            { label: 'Home', path: '/' },
            { label: 'My Account', path: '/my-account' },
            { label: 'My Access', path: '#', active: true },
          ]}
        />
        <section className="section-ptb">
          <Container>
            <Alert variant="warning">
              This page is only accessible to dealer employees.
            </Alert>
          </Container>
        </section>
      </div>
    );
  }

  return (
    <div className="site-content">
      <PageHeader
        title="My Access"
        description="Manage your dealer access permissions"
        breadCrumbItems={[
          { label: 'Home', path: '/' },
          { label: 'My Account', path: '/my-account' },
          { label: 'My Access', path: '#', active: true },
        ]}
      />
      <section className="section-ptb">
        <Container>
          {actionStatus.error && (
            <Alert variant="danger" className="mb-3">
              {actionStatus.error}
            </Alert>
          )}
          {actionStatus.success && (
            <Alert variant="success" className="mb-3">
              Access updated successfully!
            </Alert>
          )}
          <Row>
            <Col sm={12}>
              <Card>
                <Card.Header>
                  <h4 className="mb-0">Dealer Access</h4>
                </Card.Header>
                <Card.Body>
                  {error && (
                    <Alert variant="danger">
                      {error}
                      {!user?.dealer_access && (
                        <div className="mt-2">
                          <small>
                            Note: You need to accept a dealer invitation to
                            access this page. If you've recently accepted an
                            invitation, please try refreshing the page.
                          </small>
                        </div>
                      )}
                    </Alert>
                  )}

                  {isLoading ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading access information...</p>
                    </div>
                  ) : !dealerAccess ? (
                    <Alert variant="info">
                      No dealer access invitations found.
                      {user?.role === 'DealerEmployee' &&
                        !user?.dealer_access && (
                          <div className="mt-2">
                            <small>
                              You are registered as a dealer employee but
                              haven't accepted any dealer invitations yet.
                              Please check your email for pending invitations or
                              contact your dealer administrator.
                            </small>
                          </div>
                        )}
                    </Alert>
                  ) : (
                    <div className="dealer-access-info">
                      <div className="mb-3">
                        <strong>Dealer:</strong> {dealerAccess.businessName}
                      </div>
                      <div className="mb-3">
                        <strong>Email:</strong> {dealerAccess.email}
                      </div>
                      <div className="mb-3">
                        <strong>Role:</strong>{' '}
                        {getRoleBadge(dealerAccess.role || 'employee')}
                      </div>
                      <div className="mb-3">
                        <strong>Status:</strong>{' '}
                        {getStatusBadge(dealerAccess.status)}
                      </div>
                      {dealerAccess.status === 'pending' && (
                        <div className="mt-4">
                          <Button
                            variant="success"
                            className="me-2"
                            onClick={() => handleAccessAction('accept')}
                            disabled={actionStatus.loading}
                          >
                            {actionStatus.loading ? 'Processing...' : 'Accept'}
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => handleAccessAction('reject')}
                            disabled={actionStatus.loading}
                          >
                            {actionStatus.loading ? 'Processing...' : 'Reject'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default MyAccess;
